import {
  type DateMessage,
  type DateTime,
  type FieldValue,
  FieldValueExt,
  type Time,
  type UnrepresentableValue,
} from '@castoredc/castor_prose';
import { isValid } from 'date-fns';
import { isUnrepresentableValue } from './unrepresentableValue';

// Shared and never mutated: MUI rebuilds a field's typed sections whenever the value's identity changes.
export const INVALID_PICKER_DATE = new Date('');

export const convertJsDateToProseDate = (
  value: Date | null
): DateMessage | UnrepresentableValue | undefined => {
  if (value === null) {
    return undefined;
  }

  // Here getUTC functions are intentionally not used to avoid potential day change
  // (MUI DatePicker provides value of local midnight which might be different day in UTC)
  if (value instanceof Date && isValid(value)) {
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    };
  }

  return { rawData: value.toString() };
};

export const convertJsDateToProseDateTime = (
  value: Date | null
): DateTime | UnrepresentableValue | undefined => {
  if (value === null) {
    return undefined;
  }

  if (value instanceof Date && isValid(value)) {
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      hour: value.getHours(),
      minute: value.getMinutes(),
    };
  }

  return { rawData: value.toString() };
};

export const convertProseDateToJsDate = (
  value: DateMessage | UnrepresentableValue
): Date => {
  if (isUnrepresentableValue(value)) {
    return INVALID_PICKER_DATE;
  }

  const paddedYear = value.year.toString().padStart(4, '0');
  const paddedMonth = value.month.toString().padStart(2, '0');
  const paddedDay = value.day.toString().padStart(2, '0');

  // Here UTC (Z) is intentionally not used to avoid possible day change (using UTC with
  // fixed midnight might result in day change in MUI DatePicker depending on user locale).
  // String constructor with padding is used to avoid the issue of JS being "helpful" and
  // filling date into 20th century (for example: 5 as year results in 1905)...
  return new Date(`${paddedYear}-${paddedMonth}-${paddedDay}T00:00:00`);
};

export const convertProseDateTimeToJsDate = (
  value: DateTime | UnrepresentableValue
): Date => {
  if (isUnrepresentableValue(value)) {
    return INVALID_PICKER_DATE;
  }

  const paddedYear = value.year.toString().padStart(4, '0');
  const paddedMonth = value.month.toString().padStart(2, '0');
  const paddedDay = value.day.toString().padStart(2, '0');
  const paddedHour = value.hour.toString().padStart(2, '0');
  const paddedMinute = value.minute.toString().padStart(2, '0');

  // String constructor with padding is used to avoid the issue of JS being "helpful" and
  // filling date into 20th century (for example: 5 as year results in 1905)...
  return new Date(
    `${paddedYear}-${paddedMonth}-${paddedDay}T${paddedHour}:${paddedMinute}`
  );
};

export const convertJsDateToProseTime = (
  value: Date | null
): Time | UnrepresentableValue | undefined => {
  if (value === null) {
    return undefined;
  }

  if (value instanceof Date && isValid(value)) {
    return {
      hour: value.getHours(),
      minute: value.getMinutes(),
    };
  }

  return { rawData: value.toString() };
};

export const convertProseTimeToJsDate = (
  value: Time | UnrepresentableValue
): Date => {
  if (isUnrepresentableValue(value)) {
    return INVALID_PICKER_DATE;
  }

  const paddedHour = value.hour.toString().padStart(2, '0');
  const paddedMinute = value.minute.toString().padStart(2, '0');

  // Use today's date with the specified time
  // This allows MUI TimePicker to work with a valid Date object
  const today = new Date();
  const paddedYear = today.getFullYear().toString().padStart(4, '0');
  const paddedMonth = (today.getMonth() + 1).toString().padStart(2, '0');
  const paddedDay = today.getDate().toString().padStart(2, '0');

  return new Date(
    `${paddedYear}-${paddedMonth}-${paddedDay}T${paddedHour}:${paddedMinute}:00`
  );
};

export const toPickerDate = (
  value: FieldValue | undefined,
  kind: 'date' | 'dateTime' | 'time'
): Date | null => {
  if (value !== undefined && FieldValueExt.is_unrepresentable(value)) {
    return INVALID_PICKER_DATE;
  }

  switch (kind) {
    case 'date': {
      const date = FieldValueExt.as(value, 'date');
      return date ? convertProseDateToJsDate(date) : null;
    }
    case 'dateTime': {
      const dateTime = FieldValueExt.as(value, 'dateTime');
      return dateTime ? convertProseDateTimeToJsDate(dateTime) : null;
    }
    case 'time': {
      const time = FieldValueExt.as(value, 'time');
      return time ? convertProseTimeToJsDate(time) : null;
    }
  }
};
